import { useState, useEffect } from "react";
import { MapPinned, Loader2, Plus, Pencil, Trash2, ChevronRight, Home } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/pages/Residents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TYPE_LABEL = { dusun: "Dusun", rw: "RW", rt: "RT" };

export default function WilayahDesa() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dusunName, setDusunName] = useState("");
  const [adding, setAdding] = useState(false);
  const [rename, setRename] = useState(null); // { id, type, name }
  const [renameVal, setRenameVal] = useState("");
  const [toDelete, setToDelete] = useState(null); // { id, type, name }

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/village-units"); setTree(r.data || []); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addUnit = async (type, name, parent_id, done) => {
    const val = (name || "").trim();
    if (!val) { toast.error("Nama wajib diisi"); return; }
    try {
      await api.post("/village-units", { type, name: val, parent_id: parent_id || null });
      toast.success(`${TYPE_LABEL[type]} "${val}" ditambahkan`);
      done && done();
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addDusun = async () => {
    setAdding(true);
    await addUnit("dusun", dusunName, null, () => setDusunName(""));
    setAdding(false);
  };

  const doRename = async () => {
    const val = renameVal.trim();
    if (!val) { toast.error("Nama wajib diisi"); return; }
    try {
      await api.put(`/village-units/${rename.id}`, { type: rename.type, name: val });
      toast.success("Nama diperbarui");
      setRename(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/village-units/${toDelete.id}`);
      toast.success(`${TYPE_LABEL[toDelete.type]} "${toDelete.name}" dihapus`);
      setToDelete(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openRename = (u) => { setRename(u); setRenameVal(u.name); };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <PageHeader
        icon={MapPinned}
        title="Wilayah Desa"
        subtitle="Daftarkan Dusun, RW, dan RT desa Anda sekali. Data ini dipakai sebagai pilihan di form Data Warga."
      />

      {/* Add dusun */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <Label className="text-sm text-slate-600">Tambah Dusun / Dukuh / Lingkungan</Label>
        <div className="flex gap-2 mt-2">
          <Input
            value={dusunName}
            onChange={(e) => setDusunName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addDusun()}
            placeholder="Contoh: Dusun Karangrejo"
            data-testid="dusun-name-input"
            className="h-11"
          />
          <Button onClick={addDusun} disabled={adding} data-testid="add-dusun-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl h-11 shrink-0">
            {adding ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-1.5" />} Tambah Dusun
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-16 grid place-items-center"><Loader2 className="animate-spin text-[#064E3B]" size={28} /></div>
      ) : tree.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-emerald-50 grid place-items-center mx-auto mb-4"><MapPinned className="text-[#064E3B]" /></div>
          <p className="text-slate-500">Belum ada dusun. Tambahkan dusun pertama di atas untuk memulai.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="wilayah-tree">
          {tree.map((dusun) => (
            <DusunCard
              key={dusun.id}
              dusun={dusun}
              onAdd={addUnit}
              onRename={openRename}
              onDelete={setToDelete}
            />
          ))}
        </div>
      )}

      {/* Rename dialog */}
      <Dialog open={!!rename} onOpenChange={(o) => !o && setRename(null)}>
        <DialogContent className="max-w-md" data-testid="rename-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">Ubah Nama {rename && TYPE_LABEL[rename.type]}</DialogTitle></DialogHeader>
          <Input
            value={renameVal}
            onChange={(e) => setRenameVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doRename()}
            data-testid="rename-input"
            className="h-11"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRename(null)} className="rounded-xl">Batal</Button>
            <Button onClick={doRename} data-testid="rename-save-button" className="bg-[#064E3B] hover:bg-[#043E2F] rounded-xl">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="wilayah-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {toDelete && TYPE_LABEL[toDelete.type]}?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{toDelete?.name}</b> beserta seluruh unit di bawahnya akan dihapus permanen. Data warga tidak ikut terhapus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="wilayah-confirm-delete" className="bg-red-600 hover:bg-red-700 rounded-xl">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DusunCard({ dusun, onAdd, onRename, onDelete }) {
  const [rwName, setRwName] = useState("");
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`dusun-${dusun.id}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-4 bg-emerald-50/60 border-b border-emerald-100">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-[#064E3B] grid place-items-center shrink-0"><Home size={16} className="text-white" /></div>
          <span className="font-heading font-bold text-slate-900 truncate">{dusun.name}</span>
          <span className="text-xs text-slate-400 shrink-0">({dusun.children?.length || 0} RW)</span>
        </div>
        <div className="flex gap-1 shrink-0">
          <IconBtn onClick={() => onRename(dusun)} title="Ubah nama" testid={`dusun-rename-${dusun.id}`}><Pencil size={15} /></IconBtn>
          <IconBtn onClick={() => onDelete(dusun)} title="Hapus" danger testid={`dusun-delete-${dusun.id}`}><Trash2 size={15} /></IconBtn>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {(dusun.children || []).map((rw) => (
          <RwBlock key={rw.id} rw={rw} onAdd={onAdd} onRename={onRename} onDelete={onDelete} />
        ))}

        <div className="flex gap-2 pt-1">
          <Input
            value={rwName}
            onChange={(e) => setRwName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onAdd("rw", rwName, dusun.id, () => setRwName(""))}
            placeholder="Tambah RW (mis. 001)"
            data-testid={`add-rw-input-${dusun.id}`}
            className="h-9 text-sm"
          />
          <Button size="sm" variant="outline" onClick={() => onAdd("rw", rwName, dusun.id, () => setRwName(""))} data-testid={`add-rw-button-${dusun.id}`} className="rounded-lg shrink-0">
            <Plus size={14} className="mr-1" /> RW
          </Button>
        </div>
      </div>
    </div>
  );
}

function RwBlock({ rw, onAdd, onRename, onDelete }) {
  const [rtName, setRtName] = useState("");
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60" data-testid={`rw-${rw.id}`}>
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <ChevronRight size={14} className="text-slate-400 shrink-0" />
          <span className="font-semibold text-sm text-slate-800 shrink-0">RW {rw.name}</span>
          <span className="text-xs text-slate-400 shrink-0">({rw.children?.length || 0} RT)</span>
        </div>
        <div className="flex gap-1 shrink-0">
          <IconBtn onClick={() => onRename(rw)} title="Ubah nama" testid={`rw-rename-${rw.id}`}><Pencil size={13} /></IconBtn>
          <IconBtn onClick={() => onDelete(rw)} title="Hapus" danger testid={`rw-delete-${rw.id}`}><Trash2 size={13} /></IconBtn>
        </div>
      </div>

      <div className="px-3 pb-3 pl-8 space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {(rw.children || []).map((rt) => (
            <span key={rt.id} data-testid={`rt-${rt.id}`} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg bg-white border border-slate-200 text-xs text-slate-700">
              RT {rt.name}
              <button onClick={() => onRename(rt)} data-testid={`rt-rename-${rt.id}`} className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-[#064E3B]"><Pencil size={11} /></button>
              <button onClick={() => onDelete(rt)} data-testid={`rt-delete-${rt.id}`} className="p-0.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"><Trash2 size={11} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={rtName}
            onChange={(e) => setRtName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onAdd("rt", rtName, rw.id, () => setRtName(""))}
            placeholder="Tambah RT (mis. 002)"
            data-testid={`add-rt-input-${rw.id}`}
            className="h-8 text-xs max-w-[200px]"
          />
          <Button size="sm" variant="outline" onClick={() => onAdd("rt", rtName, rw.id, () => setRtName(""))} data-testid={`add-rt-button-${rw.id}`} className="rounded-lg h-8 text-xs shrink-0">
            <Plus size={13} className="mr-1" /> RT
          </Button>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-2 rounded-lg transition-colors ${danger ? "text-slate-500 hover:bg-red-50 hover:text-red-600" : "text-slate-500 hover:bg-white hover:text-[#064E3B]"}`}
    >
      {children}
    </button>
  );
}
